import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  InfoTransmissionMessage,
  RestaurantStaffInterventionStatus,
  messagingActions,
} from '../../reducers/messagingSlice';
import { selectRestaurantStaffIntervention } from '../../selectors/message';

type InterventionReason = {
  /** Stable machine key — analytics source of truth, never changes when a label is renamed. */
  code: string;
  /** Display label shown in the popup (rendered uppercase by MUI). */
  label: string;
};

const OTHER_CODE = 'OTHER';

// Column-major layout: the first 6 fill the left column top-to-bottom, the next 6 the right.
const reasons: InterventionReason[] = [
  // Left column — item, conversation & audio problems
  { code: 'ITEM_NOT_VALIDATING', label: 'Item not validating' },
  { code: 'ITEM_MISSING', label: 'Item missing' },
  { code: 'MODIFIER_NOT_SUPPORTED', label: 'Modifier not supported' },
  { code: 'CX_QUESTION_NO_ANSWER', label: "Can't answer CX question" },
  { code: 'LANGUAGE_NOT_SUPPORTED', label: 'Language not supported' },
  { code: 'AUDIO_ISSUE', label: 'Audio issue' },
  // Right column — promo, channel, menu, staff, general catch-all & Other (last)
  { code: 'COUPON_ISSUE', label: 'Coupon issue' },
  { code: 'THIRD_PARTY_MOBILE', label: '3rd party / mobile' },
  { code: 'GREEN_BURRITO_MENU', label: 'Green Burrito menu' },
  { code: 'REQUESTED_STAFF', label: 'Requested staff' },
  { code: 'ORDER_ISSUE', label: 'Order issue' },
  { code: OTHER_CODE, label: 'Other' },
];

const NOTE_MAX_LENGTH = 120;

export default function RestaurantStaffDialog() {
  const dispatch = useDispatch();
  const restaurantStaffIntervention = useSelector(
    selectRestaurantStaffIntervention
  );

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const isOpen =
    restaurantStaffIntervention === RestaurantStaffInterventionStatus.open;
  const isOtherSelected = selectedCode === OTHER_CODE;

  const resetLocalState = () => {
    setSelectedCode(null);
    setNote('');
  };

  const handleClose = () => {
    dispatch(
      messagingActions.setRestaurantStaffIntervention(
        RestaurantStaffInterventionStatus.close
      )
    );
    resetLocalState();
  };

  const emitReason = (reason: InterventionReason, freeText: string | null) => {
    const message = 'Intervention reason: ' + reason.label + ' is selected';
    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        message,
        type: 'METRIC',
        metadata: {
          event_name: 'intervention_reason',
          // `reason` carries the stable machine code — same field name as before
          // (backward compatible), just a stable value instead of the raw label.
          // Label/category are derivable from the code downstream, so we don't emit them.
          reason: reason.code,
          free_text: freeText,
        },
      },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  };

  const handleSelectReason = (reason: InterventionReason) => {
    // "Other" needs a note first — reveal the inline field instead of emitting.
    if (reason.code === OTHER_CODE) {
      setSelectedCode(OTHER_CODE);
      setNote('');
      return;
    }
    emitReason(reason, null);
    handleClose();
  };

  const handleConfirmOther = () => {
    const trimmed = note.trim();
    if (!trimmed) return;
    const other = reasons.find((r) => r.code === OTHER_CODE);
    if (other) emitReason(other, trimmed);
    handleClose();
  };

  return (
    <Dialog open={!!isOpen} PaperProps={{ sx: { maxWidth: '450px' } }}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Select an intervention reason
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'repeat(6, auto)',
            gridAutoFlow: 'column',
            gap: 1,
            mt: 0.5,
          }}
        >
          {reasons.map((reason) => (
            <Button
              key={reason.code}
              data-testid={`reason-${reason.code}`}
              variant={selectedCode === reason.code ? 'contained' : 'outlined'}
              size="large"
              onClick={() => handleSelectReason(reason)}
            >
              {reason.label}
            </Button>
          ))}
        </Box>
        {isOtherSelected && (
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Add a note"
              placeholder="Describe the reason…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirmOther();
              }}
              inputProps={{
                maxLength: NOTE_MAX_LENGTH,
                'data-testid': 'reason-other-note',
              }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!note.trim()}
              onClick={handleConfirmOther}
              sx={{ mt: 1.5 }}
              data-testid="reason-other-confirm"
            >
              Confirm
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
